import { formatNumToStandardNotation } from '@modules/common/utils';
import { QuantifyCooccurrenceToggle } from '@modules/quantify/components/QuantifyCooccurrenceToggle';
import {
  QuantifyResetToTopSelectionButton,
  QuantifyTopSelectionMenu,
} from '@modules/quantify/components/QuantifyTopSelection';
import { useIsAnomaliesTrendVisible } from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getVisibleDataPointsLimit,
  isTopSelectionSupported,
} from '@modules/quantify/utils';
import { Skeleton, Stack, Typography } from '@mui/material';
import pluralize from 'pluralize';
import { shallow } from 'zustand/shallow';
import { QuantifyReportSearchBar } from './QuantifyReportSearchBar';

export const QuantifyReportTableHeader = () => {
  const isAnomaliesTrendVisible = useIsAnomaliesTrendVisible();

  const {
    visibleDataPointsCount,
    activeVisualisationType,
    groupByLabel,
    shouldShowTopDataPoints,
    tableAggregateHits,
    isTableQueryInProgress,
  } = useQuantifyStore(({ query, visual }) => {
    return {
      visibleDataPointsCount: query.visibleDataPoints?.length ?? 0,
      activeVisualisationType: query.visualisationType,
      groupByLabel: query.groupByField?.displayName ?? '',
      shouldShowTopDataPoints: query.shouldShowTopDataPoints,
      tableAggregateHits: visual.tableAggregateHits,
      isTableQueryInProgress: visual.isTableQueryInProgress,
    };
  }, shallow);

  const selectionLimit = getVisibleDataPointsLimit(activeVisualisationType);

  const selectedRowsCount =
    visibleDataPointsCount > selectionLimit
      ? selectionLimit
      : visibleDataPointsCount;

  const areMaxRowsSelected = visibleDataPointsCount >= selectionLimit;

  const isShowTopSelectionEnabled = isTopSelectionSupported(
    activeVisualisationType,
  );

  const shouldShowTopSelectionMenu =
    isShowTopSelectionEnabled && shouldShowTopDataPoints;

  const renderTotalRowsCount = () => {
    if (isTableQueryInProgress) {
      return (
        <Skeleton
          variant="rounded"
          width={24}
          height={16}
          sx={{
            display: 'inline-block',
            verticalAlign: 'middle',
            bgcolor: 'text.disabled',
            mt: -0.3,
            mx: 0.3,
          }}
        />
      );
    }

    return <>{formatNumToStandardNotation(tableAggregateHits)}</>;
  };

  return (
    <Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
        spacing={2}
        data-testid="quantify-report-table-header"
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          rowGap={1}
          spacing={1}
          flexWrap="wrap"
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={shouldShowTopSelectionMenu ? 1 : 0.5}
            >
              <Typography variant="body2">Selected</Typography>
              {shouldShowTopSelectionMenu ? (
                <QuantifyTopSelectionMenu />
              ) : (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    display: 'inline',
                  }}
                >
                  {selectedRowsCount}
                </Typography>
              )}
              <Typography variant="body2">
                of {renderTotalRowsCount()}{' '}
                {`${isAnomaliesTrendVisible ? 'anomalous ' : ''}${pluralize(
                  groupByLabel,
                )}`}
              </Typography>
              <QuantifyResetToTopSelectionButton />
            </Stack>
            {areMaxRowsSelected && (
              <Typography color="text.disabled" variant="body2">
                Max values selected
              </Typography>
            )}
          </Stack>
          <QuantifyCooccurrenceToggle />
        </Stack>
      </Stack>
      <QuantifyReportSearchBar />
    </Stack>
  );
};
